import { BaseModelType } from "../BaseModel";
import { ModelContext } from "../engine";
import { AudienceLevel, TileGenre } from "../spaces/types";
import { BlueprintElement, BlueprintElementRole } from "./BlueprintElement";

/**
 * ModuleBlueprint: abstract definition  of an engine-powered artifact (fields, structure, behavior).
 * Declares what the feature does and which concepts (variable prototypes) it involves.
 * For DAG-dependent modules, the blueprint will be referenced by a context.
 */
export type BlueprintConfig = {
    /**
     * Named groups of elements, keyed by the `group_key` its members carry.
     *
     * Two blueprint types use the same mechanism to say different things. On EVALUATION a group is
     * a comparison row — a POSITION with its EVIDENCE — and its rendering is fixed by
     * `useEvaluationPositionLayout`. On THEME a group is one tile of a MOSAIC surface, and `genre`
     * says how it draws; that key is meaningful on THEME alone and the server refuses it elsewhere.
     */
    element_groups?: Record<
        string,
        {
            label?: string;
            /** THEME only. Absent on an EVALUATION group, and on a THEME group not yet drawn. */
            genre?: TileGenre;
        }
    >;
};

export type ModuleBlueprint = BaseModelType & {
    blueprint_type: BlueprintType; // set tbd
    // consider template_type: "SCORECARD" | "REPORT" | ... to go with TEMPLATE blueprint_type
    label: string;
    weight: number;
    description?: string;
    config?: BlueprintConfig;
    blueprint_key?: string; // nullable but unique

    /** Absent means GENERAL — the baseline, not "unset". */
    audience_level?: AudienceLevel;

    // field ports
    blueprintElements?: BlueprintElement[];
    // archetypes
    blueprint_elements_count?: number;
    workspace_resources_count?: number;
    orchestrator_items_count?: number;

    modelContext?: ModelContext;
};

// export type BlueprintType =
//     | "THEME" // guided content / intervention pattern; prototype-driven
//     | "SCORECARD TEMPLATE" // authored sections + rules; rendered by SCORECARD surfaces
//     | "REPORT TEMPLATE" // authored report layout/sections; rendered by REPORT surfaces
//     | "TOOL TEMPLATE"; // interactive, may later require DAG/context bindings

export enum BlueprintType {
    THEME = "THEME",
    ANALYSIS = "ANALYSIS",
    EVALUATION = "EVALUATION",
    TOOL = "TOOL",
    FORM = "FORM",
}

/**
 * Which types may carry element groups — the client's copy of
 * `App\Enums\Engine\BlueprintType::supportsElementGroups()`.
 *
 * **A hand-written second statement of a server fact, and it went stale the moment the server
 * changed.** `THEME` was commented out here as deferred; when the PHP admitted it, this list still
 * said no — and because it gates both the group manager and the element editor's group select, the
 * server accepted a `group_key` on a THEME element that no admin screen could ever send. A gate is
 * exactly the kind of duplicate that fails silently in the permissive direction on one side and the
 * restrictive direction on the other.
 *
 * It cannot be generated: `supportsElementGroups()` is a method, and `typescript:transform` emits
 * enum *cases*, not behaviour. So the two are pinned by a test that reads the PHP
 * (`blueprintCapabilities.test.ts`), which is the only instrument that can see across the boundary.
 */
export const BLUEPRINT_TYPES_WITH_GROUPS: BlueprintType[] = [
    BlueprintType.EVALUATION,
    BlueprintType.THEME,
];

export function supportsElementGroups(type: BlueprintType): boolean {
    return BLUEPRINT_TYPES_WITH_GROUPS.includes(type);
}

/**
 * Which types' element groups carry a {@link TileGenre} — mirrors `BlueprintType::supportsTileGenres()`.
 *
 * Narrower than the above on purpose: an EVALUATION group is a comparison row whose rendering is
 * fixed by `useEvaluationPositionLayout`, so a genre there would be a key nothing reads, and the
 * server rejects one outright.
 */
export const BLUEPRINT_TYPES_WITH_GENRES: BlueprintType[] = [
    BlueprintType.THEME,
];

export function supportsTileGenres(type: BlueprintType): boolean {
    return BLUEPRINT_TYPES_WITH_GENRES.includes(type);
}

// THEME      = what cluster of concepts matters          - COLLECTION  (prototype-driven)
// TOOL       = what can we change / simulate             - INTERACTION (prototype-driven, DAG-bound)
// ANALYSIS   = why this happens / what story can we tell - NARRATION   (prototype-driven)
// EVALUATION = how are we doing relative to reference    - COMPARISON  (prototype-driven)
// FORM       = intake for goals, sentiment, priorities   - DATA INPUT  (context-driven; elements optional for numerical prototype links)

export const BlueprintTypeElementRoleMap: Record<
    BlueprintType,
    BlueprintElementRole[]
> = {
    [BlueprintType.THEME]: [
        BlueprintElementRole.SIGNAL,
        BlueprintElementRole.FOCUS,
        BlueprintElementRole.CONTEXT,
    ],
    [BlueprintType.TOOL]: [
        BlueprintElementRole.LEVER,
        BlueprintElementRole.PARAMETER,
        BlueprintElementRole.INTERMEDIATE,
        BlueprintElementRole.OUTPUT,
    ],
    [BlueprintType.ANALYSIS]: [
        // also FOCUS or separate MAIN / PRIMARY role?
        BlueprintElementRole.DRIVER,
        BlueprintElementRole.FACTOR,
        BlueprintElementRole.OUTCOME,
    ],
    [BlueprintType.EVALUATION]: [
        BlueprintElementRole.SUBJECT,
        BlueprintElementRole.POSITION,
        BlueprintElementRole.EVIDENCE,
    ],
    // FORM: QUESTION-role elements link NUMERICAL_SLIDER variables to canonical prototypes
    // (cross-source identity without goal commitment). GOAL-role elements additionally drive
    // goal MetricValue generation via the prototype → metric_variable chain.
    // Non-numerical form variables do not need elements.
    [BlueprintType.FORM]: [
        BlueprintElementRole.QUESTION,
        BlueprintElementRole.GOAL,
    ],
};

/**
 * Element roles that may appear at most once per blueprint of the given type.
 * Used on the frontend to prevent creating duplicate elements with a unique role.
 * Extend this map as new constraints are needed for other blueprint types / roles.
 */
export const BlueprintTypeUniqueElementRoles: Partial<
    Record<BlueprintType, BlueprintElementRole[]>
> = {
    // [BlueprintType.EVALUATION]: [
    //     BlueprintElementRole.SUBJECT,
    //     BlueprintElementRole.POSITION,
    // ],
};

// /**
//  * earlier idea about intermediate model: concrete installation of a blueprint that binds it to
//  * a context and/or other data universes so it can run (and no need for MV id on elements).
//  */
// export type ModuleInstance = {
//     id: string;
//     module_blueprint_id: string;
//     model_context_id?: string; // calc-dependent
//     config?: Record<string, any>;
// };
